import type { CSSProperties, FC, ReactNode } from 'react'

import { useState } from 'react'
import { motion } from 'motion/react'
import { BadgeCheck, RotateCcw, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EASE_OUT_EXPO } from '@/components/motion/reveal'
import { Logo } from '@/components/generals/logo'

/* SoftApproval: flagged buyers meet a challenge; those who pass are released. */

const FLOW = Array.from({ length: 14 }, (_, index) => ({
    lane: index % 7,
    delay: index * 0.62,
    fails: index % 5 === 2
}))

export const SoftApprovalVisual: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <div aria-hidden className={cn('relative flex flex-col gap-6', className)}>
            <div className='flex justify-between t-label text-tone-muted'>
                <span>
                    Flagged buyers
                </span>
                <span>
                    Challenge
                </span>
                <span>
                    Released
                </span>
            </div>

            <div className='relative h-64 border-y border-tone-line md:h-72'>
                {Array.from({ length: 7 }, (_, lane) => (
                    <span key={lane} className='absolute inset-x-0 h-px bg-tone-line/60' style={{ top: `${(lane + 1) * 12.5}%` }} />
                ))}

                <div className='absolute inset-y-[-12px] left-1/2 w-24 -translate-x-1/2 border-x border-dashed border-tone-accent/60 bg-tone-accent/10'>
                    <span className='absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap t-label text-tone-accent'>
                        Unique to buyer profile
                    </span>
                </div>

                {FLOW.map((dot, index) => (
                    <span
                        key={index}
                        className='absolute size-2.5 -translate-y-1/2 rounded-full'
                        style={{
                            top: `${(dot.lane + 1) * 12.5}%`,
                            animation: `${dot.fails ? 'flow-fail' : 'flow-pass'} 8.6s ${dot.delay}s cubic-bezier(0.45, 0, 0.2, 1) infinite both`
                        } as CSSProperties}
                    />
                ))}
            </div>

            <div className='flex flex-wrap gap-x-6 gap-y-2 t-label text-tone-muted'>
                <span className='flex items-center gap-2'>
                    <span className='size-2 rounded-full bg-teal' />
                    Passed, products released
                </span>
                <span className='flex items-center gap-2'>
                    <span className='size-2 rounded-full bg-ember' />
                    Fraudster, stopped
                </span>
            </div>
        </div>
    )
}

/* StingBack: what the fraudster sees, and what actually happened. */

export const StingBackVisual: FC<{ className?: string }> = ({ className }): ReactNode => {
    const [reality, setReality] = useState(false)

    return (
        <div className={cn('flex flex-col items-start gap-6', className)}>
            <div className='flex border border-tone-line p-1' role='group' aria-label='Choose a view'>
                {[
                    { label: 'What the fraudster sees', value: false },
                    { label: 'What really happened', value: true }
                ].map(option => (
                    <button
                        key={option.label}
                        type='button'
                        aria-pressed={reality === option.value}
                        onClick={() => setReality(option.value)}
                        className={cn(
                            'px-3 py-2 t-label transition-colors duration-300 md:px-4',
                            reality === option.value ? 'bg-tone text-tone-bg' : 'text-tone-muted hover:text-tone'
                        )}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            <div className='w-full max-w-md [perspective:1400px]'>
                <motion.div
                    className='relative grid [transform-style:preserve-3d]'
                    animate={{ rotateY: reality ? 180 : 0 }}
                    transition={{ duration: 1, ease: EASE_OUT_EXPO }}
                >
                    <div className='col-start-1 row-start-1 flex flex-col gap-6 bg-mist p-7 text-void [backface-visibility:hidden] md:p-9' aria-hidden={reality}>
                        <div className='flex items-center justify-between'>
                            <span className='t-label text-void/50'>
                                Order 48213
                            </span>
                            <span className='flex items-center gap-2 t-label text-[#127a73]'>
                                <BadgeCheck className='size-4' aria-hidden />
                                Confirmed
                            </span>
                        </div>
                        <div>
                            <p className='font-heading text-3xl font-semibold tracking-tight'>
                                Digital gift card
                            </p>
                            <p className='mt-1 text-sm text-void/60'>
                                Delivered instantly to your inbox
                            </p>
                        </div>
                        <div className='border border-dashed border-void/25 px-4 py-5 text-center font-mono text-xl tracking-[0.18em] md:text-2xl'>
                            0000-0000-0000
                        </div>
                        <div className='flex items-end justify-between border-t border-void/10 pt-4'>
                            <span className='t-label text-void/50'>
                                Card value
                            </span>
                            <span className='font-heading text-4xl font-semibold tabular-nums tracking-tight'>
                                $0.00
                            </span>
                        </div>
                    </div>

                    <div className='col-start-1 row-start-1 flex flex-col justify-between gap-6 bg-violet p-7 text-white [backface-visibility:hidden] [transform:rotateY(180deg)] md:p-9' aria-hidden={!reality}>
                        <div className='flex items-center justify-between'>
                            <Logo className='h-5 w-auto' />
                            <span className='border border-white/40 px-2 py-1 t-label'>
                                StingBack®
                            </span>
                        </div>
                        <ul className='flex flex-col gap-4 text-[0.9375rem] leading-snug'>
                            <li className='flex gap-3'>
                                <ShieldCheck className='mt-0.5 size-4 shrink-0' aria-hidden />
                                A zero-value key was delivered instead of real goods.
                            </li>
                            <li className='flex gap-3'>
                                <RotateCcw className='mt-0.5 size-4 shrink-0' aria-hidden />
                                The transaction is refunded, so the cardholder isn’t billed for undelivered goods.
                            </li>
                            <li className='flex gap-3'>
                                <BadgeCheck className='mt-0.5 size-4 shrink-0' aria-hidden />
                                The fraudster’s time is wasted, and their reputation takes the hit when they try to resell.
                            </li>
                        </ul>
                        <p className='font-serif text-2xl italic leading-tight'>
                            Massive waste of time. Worth every penny.
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}

/* DynamicKYC: an identity document where everything not strictly needed stays redacted. */

const ID_FIELDS = [
    { label: 'Surname', width: '72%' },
    { label: 'Given names', width: '58%' },
    { label: 'Date of birth', width: '38%' },
    { label: 'Address', width: '86%' },
    { label: 'Document no.', width: '46%' }
]

export const DynamicKycVisual: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <div aria-hidden className={cn('w-full max-w-lg', className)}>
            <div className='relative overflow-hidden border border-tone-line bg-tone-surface p-6 md:p-8'>
                <div className='mb-6 flex items-center justify-between t-label text-tone-muted'>
                    <span>
                        Identity document
                    </span>
                    <span>
                        Need to know only
                    </span>
                </div>

                <motion.div
                    className='flex gap-6'
                    initial='hidden'
                    whileInView='visible'
                    viewport={{ once: true, margin: '0px 0px -20% 0px' }}
                >
                    <div className='relative aspect-[3/4] w-24 shrink-0 overflow-hidden bg-tone/10 md:w-28'>
                        <motion.span
                            className='absolute inset-0 origin-left bg-tone'
                            variants={{
                                hidden: { scaleX: 0 },
                                visible: { scaleX: 1, transition: { duration: 0.8, ease: EASE_OUT_EXPO, delay: 0.1 } }
                            }}
                        />
                    </div>

                    <ul className='flex flex-1 flex-col gap-4'>
                        {ID_FIELDS.map((field, index) => (
                            <li key={field.label} className='flex flex-col gap-1.5'>
                                <span className='t-label text-tone-muted'>
                                    {field.label}
                                </span>
                                <span className='relative block h-3.5 bg-tone/10'>
                                    <motion.span
                                        className='absolute inset-y-0 left-0 origin-left bg-tone'
                                        style={{ width: field.width }}
                                        variants={{
                                            hidden: { scaleX: 0 },
                                            visible: { scaleX: 1, transition: { duration: 0.8, ease: EASE_OUT_EXPO, delay: 0.25 + index * 0.12 } }
                                        }}
                                    />
                                </span>
                            </li>
                        ))}
                    </ul>
                </motion.div>

                <motion.div
                    className='absolute bottom-5 right-5 rotate-[-8deg] border-2 border-tone-accent px-3 py-1.5 font-heading text-sm font-bold uppercase tracking-[0.2em] text-tone-accent md:bottom-7 md:right-7'
                    initial={{ opacity: 0, scale: 1.6 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: '0px 0px -20% 0px' }}
                    transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: 1.1 }}
                >
                    No TMI
                </motion.div>
            </div>
        </div>
    )
}
